// The Standoff — game data
// Same family as Signal Boost and Moat Runner: no budget math, no resource
// pools. The skill here is negotiation — reading a tactic as it's happening,
// recognizing the psychology underneath the numbers, and holding your nerve
// through the moment that decides whether a deal actually works for both
// sides. Three stages: Read The Table (spot the tactic) -> Between The Lines
// (spot the underlying principle) -> The Standoff (high-stakes decisions) ->
// Deal Report.

// ---------------------------------------------------------------------------
// STAGE 1 — READ THE TABLE: six tactical maneuvers, the kind a counterpart
// runs on you whether you notice or not. Eight real moments, each showing
// exactly one tactic in action.
// ---------------------------------------------------------------------------
export const TACTICS = [
  { id: "ANCHOR", label: "Anchoring", icon: "⚓" },
  { id: "MIRROR", label: "Mirroring", icon: "🪞" },
  { id: "SILENCE", label: "Tactical Silence", icon: "🤐" },
  { id: "FLINCH", label: "The Flinch", icon: "😲" },
  { id: "NIBBLE", label: "The Nibble", icon: "🐭" },
  { id: "LABEL", label: "Labeling", icon: "🏷️" },
];

export const TABLE_MOMENTS = [
  {
    id: "t1",
    text: "Before you can even open your mouth, the buyer names a number so low it's almost insulting — and somehow every counteroffer you make afterward keeps drifting back toward it anyway.",
    correct: "ANCHOR",
    explain: "The first number spoken sets the mental reference point everyone measures against, even when it's an obvious lowball.",
  },
  {
    id: "t2",
    text: "You mention the contract runs \"...through the end of next quarter.\" The other side just repeats it back — \"...through the end of next quarter?\" — then goes quiet.",
    correct: "MIRROR",
    explain: "Repeating the last few words as a question is an automatic prompt for the human brain to explain more — and you just kept talking.",
  },
  {
    id: "t3",
    text: "The instant you finally state your number, the room goes completely silent for what feels like an entire minute. Nobody blinks. Nobody fills the space.",
    correct: "SILENCE",
    explain: "Tactical silence turns the awkward gap into pressure — most people can't resist filling it, often by negotiating against themselves.",
  },
  {
    id: "t4",
    text: "The instant you name your price, the other person actually recoils and lets out an audible \"...whoa, really?\" before you've even finished the sentence.",
    correct: "FLINCH",
    explain: "A visible reaction of surprise — even a rehearsed one — plants doubt about whether your number was ever reasonable.",
  },
  {
    id: "t5",
    text: "Right as you're both reaching for pens to sign, they casually mention, \"oh, and delivery's included too, right?\"",
    correct: "NIBBLE",
    explain: "One small ask right at the moment of closing works because nobody wants to reopen a deal they're already mentally finished with.",
  },
  {
    id: "t6",
    text: "\"It sounds like you're worried this timeline doesn't leave any room for a second review,\" the negotiator says, and the tension in the room visibly drops a notch.",
    correct: "LABEL",
    explain: "Naming an emotion out loud — \"it sounds like...\" — takes some of its power away, without ever arguing about whether it's justified.",
  },
  {
    id: "t7",
    text: "They open bidding at a number so far above market rate that your own realistic number suddenly feels like it's the one that needs justifying.",
    correct: "ANCHOR",
    explain: "It doesn't matter that the number was unrealistic — it still shifted where \"reasonable\" felt like it lived for the rest of the conversation.",
  },
  {
    id: "t8",
    text: "You say \"we need this delivered by Friday,\" and they just echo, \"by Friday?\" — leaving enough silence that you keep explaining why.",
    correct: "MIRROR",
    explain: "A single echoed phrase, followed by silence, reliably pulls more information out of the other person than a direct question would.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — BETWEEN THE LINES: six foundational and psychological principles
// underneath the tactics. Six scenarios, each landing in exactly one concept.
// ---------------------------------------------------------------------------
export const CONCEPTS = [
  { id: "BATNA", label: "BATNA", subtitle: "Best Alternative to a Negotiated Agreement" },
  { id: "WALKAWAY", label: "Walkaway Power", subtitle: "The party who cares least usually wins" },
  { id: "INTERESTS", label: "Interests vs. Positions", subtitle: "The why behind the want" },
  { id: "LOSS_AVERSION", label: "Loss Aversion", subtitle: "A loss stings roughly twice as hard as an equal gain" },
  { id: "TACTICAL_EMPATHY", label: "Tactical Empathy", subtitle: "Naming their view calms the room" },
  { id: "ENTROPY", label: "Entropy", subtitle: "Letting venting burn itself out" },
];

export const READ_SCENARIOS = [
  {
    id: "r1",
    text: "Before walking into a salary negotiation, you've already lined up two other job offers you'd genuinely be happy to accept instead.",
    correct: "BATNA",
    explain: "A real backup plan, built before the conversation starts, is what keeps you from negotiating out of desperation.",
  },
  {
    id: "r2",
    text: "Halfway through the negotiation, you realize you'd honestly be fine just walking out and buying the same thing somewhere else tomorrow — and that realization alone changes how you're sitting in the chair.",
    correct: "WALKAWAY",
    explain: "The party who genuinely cares least about closing the deal usually has the most leverage in the room, whether or not they say it out loud.",
  },
  {
    id: "r3",
    text: "Two coworkers both want the last conference ticket. One just wants to attend the keynote; the other just wants the swag bag from registration. It turns out they can both get exactly what they want.",
    correct: "INTERESTS",
    explain: "The classic \"orange\" problem — arguing over positions (\"I want the ticket\") hides the interests underneath that actually let both sides win.",
  },
  {
    id: "r4",
    text: "The pitch isn't framed as \"save $500\" — it's framed as \"don't lose the $500 you'd otherwise be throwing away every year.\"",
    correct: "LOSS_AVERSION",
    explain: "The exact same number lands harder framed as an avoidable loss than as an equivalent gain — people feel losses roughly twice as strongly.",
  },
  {
    id: "r5",
    text: "\"It sounds like this delay has put your whole team in a really tough spot\" — said before a single number gets discussed.",
    correct: "TACTICAL_EMPATHY",
    explain: "Naming the other side's perspective out loud, and meaning it, calms a tense room before you ever get to terms.",
  },
  {
    id: "r6",
    text: "The client spends the first fifteen minutes of the call furious about a shipping delay. You just let them talk. By minute sixteen, they're the one asking what a fair fix would look like.",
    correct: "ENTROPY",
    explain: "Letting frustration vent itself out, uninterrupted, tends to burn off the negative energy and leave a much more cooperative person behind.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 3 — THE STANDOFF: six high-stakes moments where the boring, correct
// move competes against the tempting, exposed one. Each has one clean
// answer, one merely survivable one, and one that quietly gives up ground.
// ---------------------------------------------------------------------------
export const STANDOFF_ENCOUNTERS = [
  {
    id: "s1",
    title: "The Objective Number",
    text: "You're deadlocked on a used car price — your offer and the seller's are $1,500 apart, and neither of you is budging on gut feeling alone.",
    choices: [
      {
        id: "objective-criteria",
        label: "Pull up three independent listings for the same model, mileage, and condition, and use that range as the reference point",
        tag: "good",
        result: "Objective criteria took the standoff out of \"my willpower vs. yours\" and gave you both something neutral to agree against.",
      },
      {
        id: "split-difference",
        label: "Just split the difference down the middle to end the awkwardness",
        tag: "weak",
        result: "It ended the deadlock, but you never actually found out which side of that midpoint was closer to fair.",
      },
      {
        id: "dig-in",
        label: "Dig in on your original number and repeat it louder, hoping they cave first",
        tag: "bad",
        result: "Nobody moved, the deal nearly died, and \"louder\" was never actually an argument.",
      },
    ],
  },
  {
    id: "s2",
    title: "The Personal Attack",
    text: "Mid-negotiation, the other side snaps: \"Honestly, I don't think you know what you're talking about.\"",
    choices: [
      {
        id: "jujitsu",
        label: "Sidestep it — ask what specifically is making them uneasy about the proposal itself, not the insult",
        tag: "good",
        result: "Negotiation jujitsu redirected the attack back onto the actual problem instead of a fight about who's smarter.",
      },
      {
        id: "ignore-it",
        label: "Ignore the comment completely and just keep reciting your numbers",
        tag: "weak",
        result: "You didn't take the bait, but you also didn't defuse it — the tension sat in the room for the rest of the call.",
      },
      {
        id: "fire-back",
        label: "Fire back with an equally sharp comment about their competence",
        tag: "bad",
        result: "You \"won\" the exchange and lost the deal — the relationship didn't survive the next round.",
      },
    ],
  },
  {
    id: "s3",
    title: "Good Cop, Bad Cop",
    text: "One negotiator across the table keeps hammering you on price while his colleague quietly apologizes for how \"harsh\" his partner is being.",
    choices: [
      {
        id: "name-the-tactic",
        label: "Name the pattern directly — \"it seems like you two are running good cop, bad cop here\" — and watch the dynamic dissolve",
        tag: "good",
        result: "Tactics named out loud tend to stop working immediately — nobody can keep playing a role once it's been called.",
      },
      {
        id: "deal-with-nice-one",
        label: "Just deal exclusively with the \"nice\" one from now on, hoping for a better deal through them",
        tag: "weak",
        result: "It felt safer, but you were still negotiating inside a script you never actually broke.",
      },
      {
        id: "get-rattled",
        label: "Get visibly rattled by the \"bad cop\" and offer a concession just to make the tension stop",
        tag: "bad",
        result: "The tactic worked exactly as designed — the discomfort, not the merits of the deal, is what moved you.",
      },
    ],
  },
  {
    id: "s4",
    title: "The Free Concession",
    text: "You're willing to throw in free installation to help close the deal, and you're about to just offer it outright.",
    choices: [
      {
        id: "trade-it",
        label: "Offer it conditionally — \"if I include free installation, can we agree to sign today?\"",
        tag: "good",
        result: "Trading instead of giving kept the concession from being a one-way gift — it bought you something concrete in return.",
      },
      {
        id: "give-then-mention",
        label: "Offer it for free, but mention afterward that you \"normally charge for that\"",
        tag: "weak",
        result: "You gave up the leverage the moment you handed it over — the comment afterward didn't get any of it back.",
      },
      {
        id: "give-freely",
        label: "Offer it for free with no conditions at all, just to seem generous",
        tag: "bad",
        result: "It felt generous in the moment. It also taught them your concessions come with no cost to ask for.",
      },
    ],
  },
  {
    id: "s5",
    title: "Soft on the Person, Hard on the Problem",
    text: "A long-time vendor you genuinely like is pushing a price increase you can't accept without risking the account you actually value.",
    choices: [
      {
        id: "soft-hard",
        label: "Stay warm and appreciative toward them personally, while being direct and firm that the number itself doesn't work",
        tag: "good",
        result: "The relationship stayed intact and the problem still got addressed head-on — those two things aren't actually in conflict.",
      },
      {
        id: "avoid-friction",
        label: "Accept the increase to avoid any friction with someone you like",
        tag: "weak",
        result: "The relationship stayed comfortable. So did an increase you couldn't really afford to accept.",
      },
      {
        id: "go-cold",
        label: "Get cold and distant toward them to signal your displeasure with the number",
        tag: "bad",
        result: "The message about the price got tangled up with a relationship that didn't need to take the hit.",
      },
    ],
  },
  {
    id: "s6",
    title: "Nothing to Lose",
    text: "You've walked into a negotiation with no backup plan at all — this is the only offer on the table, and you need it to work.",
    choices: [
      {
        id: "build-batna",
        label: "Pause the conversation, go build at least one real alternative first, then come back to the table",
        tag: "good",
        result: "A BATNA built before you're back in the room is what turns \"need this to work\" into \"this needs to work for me too.\"",
      },
      {
        id: "hide-desperation",
        label: "Proceed anyway, but try hard not to let your desperation show",
        tag: "weak",
        result: "You hid it well. It still shaped every number you were willing to accept.",
      },
      {
        id: "accept-anything",
        label: "Accept whatever terms are offered, since you don't have anywhere else to go",
        tag: "bad",
        result: "Without an alternative, \"walkaway power\" wasn't a strategy you had access to — and it showed in the final number.",
      },
    ],
  },
];
